import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import podService from '../services/podService';

// ── Signature canvas ──────────────────────────────────────────────────────────
function SignatureCanvas({ onSigned }) {
  const canvasRef = useRef(null);
  const drawing   = useRef(false);
  const lastPoint = useRef(null);

  const getPoint = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const src  = e.touches ? e.touches[0] : e;
    return {
      x: (src.clientX - rect.left) * (canvas.width  / rect.width),
      y: (src.clientY - rect.top)  * (canvas.height / rect.height),
    };
  };

  const startDraw = (e) => { drawing.current = true; lastPoint.current = getPoint(e, canvasRef.current); e.preventDefault(); };
  const draw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pt  = getPoint(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
    ctx.lineTo(pt.x, pt.y);
    ctx.strokeStyle = '#000'; ctx.lineWidth = 2; ctx.lineCap = 'round';
    ctx.stroke();
    lastPoint.current = pt;
  };
  const endDraw = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onSigned(canvasRef.current.toDataURL('image/png'));
  };
  const clear = () => {
    canvasRef.current.getContext('2d').clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    onSigned(null);
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={560} height={160}
        style={{ width: '100%', height: 'auto', border: '1px solid #ced4da', borderRadius: 6, background: '#fff', touchAction: 'none', cursor: 'crosshair' }}
        onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
        onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}
      />
      <button type="button" className="btn btn-outline-secondary btn-sm mt-1" onClick={clear}>Clear</button>
    </div>
  );
}

// ── Order summary ─────────────────────────────────────────────────────────────
function OrderSummary({ order }) {
  const items = order?.Items || [];
  if (!items.length) return null;
  const total = items.reduce((sum, it) => sum + (parseFloat(it.unitPriceSnapshot || 0) * (it.quantity || 0)), 0);
  const fmt   = (n) => `$${n.toFixed(2)}`;

  return (
    <div className="card mb-3" style={{ borderColor: '#dee2e6' }}>
      <div className="card-header py-2 fw-semibold" style={{ fontSize: 13, background: '#f8f9fa' }}>
        Items to be Delivered
      </div>
      <div className="card-body p-0">
        <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
          <thead style={{ background: '#f8f9fa' }}>
            <tr>
              <th style={{ paddingLeft: 12, fontWeight: 600, fontSize: 12, borderBottom: '1px solid #dee2e6' }}>Product</th>
              <th className="text-center" style={{ fontWeight: 600, fontSize: 12, borderBottom: '1px solid #dee2e6', width: 50 }}>Qty</th>
              <th className="text-end" style={{ paddingRight: 12, fontWeight: 600, fontSize: 12, borderBottom: '1px solid #dee2e6', width: 80 }}>Unit</th>
              <th className="text-end" style={{ paddingRight: 12, fontWeight: 600, fontSize: 12, borderBottom: '1px solid #dee2e6', width: 90 }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id}>
                <td style={{ paddingLeft: 12, verticalAlign: 'middle' }}>{it.Product?.name || '—'}</td>
                <td className="text-center" style={{ verticalAlign: 'middle' }}>{it.quantity}</td>
                <td className="text-end" style={{ paddingRight: 12, verticalAlign: 'middle', color: '#6c757d' }}>{fmt(parseFloat(it.unitPriceSnapshot || 0))}</td>
                <td className="text-end" style={{ paddingRight: 12, verticalAlign: 'middle' }}>{fmt(parseFloat(it.unitPriceSnapshot || 0) * (it.quantity || 0))}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: '2px solid #dee2e6' }}>
              <td colSpan={3} style={{ paddingLeft: 12, fontWeight: 700, fontSize: 13 }}>Order Total</td>
              <td className="text-end fw-bold" style={{ paddingRight: 12, fontSize: 14, color: '#198754' }}>{fmt(total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// ── Photo tab ─────────────────────────────────────────────────────────────────
function PhotoTab({ orderId, onDone }) {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [file,    setFile]    = useState(null);
  const [notes,   setNotes]   = useState('');
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setError('');
  };

  const submit = async () => {
    if (!file) { setError('Please select or take a photo first.'); return; }
    setSaving(true); setError('');
    try {
      const { uploadUrl, key } = await podService.getAdminUploadUrl(orderId, file.type, file.size);
      await axios.put(uploadUrl, file, { headers: { 'Content-Type': file.type } });
      await podService.saveAdminPhotoPod(orderId, key, notes || null);
      onDone('photo');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to upload photo.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {preview ? (
        <div className="mb-3 text-center">
          <img src={preview} alt="preview" style={{ maxWidth: '100%', maxHeight: 240, borderRadius: 8, border: '1px solid #dee2e6' }} />
          <div className="mt-1">
            <button className="btn btn-outline-secondary btn-sm" onClick={() => { setPreview(null); setFile(null); fileRef.current.value = ''; }}>
              Retake
            </button>
          </div>
        </div>
      ) : (
        <div className="d-grid mb-3">
          <button className="btn btn-outline-primary" style={{ padding: 18, fontSize: 14 }} onClick={() => fileRef.current.click()}>
            📷 Select / Take Photo
          </button>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={onFileChange} />
        </div>
      )}

      <div className="mb-3">
        <label className="form-label" style={{ fontSize: 13 }}>Notes (optional)</label>
        <textarea className="form-control form-control-sm" rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any notes about this delivery…" />
      </div>

      {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}

      <button className="btn btn-success w-100" onClick={submit} disabled={saving || !file}>
        {saving ? <><span className="spinner-border spinner-border-sm me-2" />Uploading…</> : 'Submit Photo POD'}
      </button>
    </div>
  );
}

// ── Signature tab ─────────────────────────────────────────────────────────────
function SignatureTab({ orderId, onDone }) {
  const [name,   setName]   = useState('');
  const [sigData, setSigData] = useState(null);
  const [notes,  setNotes]  = useState('');
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');
  const gpsRef = useRef({ latitude: null, longitude: null, ready: false });

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      pos => { gpsRef.current = { latitude: pos.coords.latitude, longitude: pos.coords.longitude, ready: true }; },
      () => { gpsRef.current.ready = true; },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  }, []);

  const submit = async () => {
    if (!name.trim()) { setError("Please enter the recipient's name."); return; }
    if (!sigData)     { setError('Please have the customer sign above.'); return; }
    setSaving(true); setError('');

    if (!gpsRef.current.ready) {
      await new Promise(resolve => {
        const deadline = setTimeout(resolve, 3000);
        const check = setInterval(() => {
          if (gpsRef.current.ready) { clearInterval(check); clearTimeout(deadline); resolve(); }
        }, 200);
      });
    }

    const { latitude, longitude } = gpsRef.current;
    try {
      await podService.saveAdminSignaturePod({
        orderId,
        signedByName: name.trim(),
        signatureDataUrl: sigData,
        latitude,
        longitude,
        receiverNotes: notes || null,
      });
      onDone('signature');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save signature.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="mb-3">
        <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Recipient Name <span className="text-danger">*</span></label>
        <input className="form-control form-control-sm" value={name} onChange={e => setName(e.target.value)} placeholder="Full name of person receiving the order" />
      </div>

      <div className="mb-3">
        <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Signature <span className="text-danger">*</span></label>
        <p className="text-muted mb-1" style={{ fontSize: 12 }}>Customer signs below.</p>
        <SignatureCanvas onSigned={setSigData} />
      </div>

      <div className="mb-3">
        <label className="form-label" style={{ fontSize: 13 }}>Notes (optional)</label>
        <textarea className="form-control form-control-sm" rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any delivery notes…" />
      </div>

      {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}

      <button className="btn btn-success w-100" onClick={submit} disabled={saving}>
        {saving ? <><span className="spinner-border spinner-border-sm me-2" />Saving…</> : 'Capture & Generate Receipt'}
      </button>
    </div>
  );
}

// ── Main modal ────────────────────────────────────────────────────────────────
export default function PodCaptureModal({ order, onClose, onCaptured }) {
  const [tab,  setTab]  = useState('photo');
  const [done, setDone] = useState(null);

  if (!order) return null;

  const handleDone = (method) => {
    setDone(method);
    if (onCaptured) onCaptured(method);
  };

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Capture Proof of Delivery — #{order.orderNumber}</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            {!done && <OrderSummary order={order} />}
            {done ? (
              <div className="text-center py-4">
                <div style={{ fontSize: 48 }}>{done === 'signature' ? '✅' : '📎'}</div>
                <h5 className="mt-3 fw-bold">
                  {done === 'signature' ? 'Signature captured!' : 'Photo submitted!'}
                </h5>
                <p className="text-muted" style={{ fontSize: 14 }}>Proof of delivery has been recorded.</p>
                <button className="btn btn-primary mt-1" onClick={onClose}>Close</button>
              </div>
            ) : (
              <>
                <ul className="nav nav-tabs mb-3">
                  <li className="nav-item">
                    <button className={`nav-link ${tab === 'photo' ? 'active' : ''}`} onClick={() => setTab('photo')}>
                      📷 Photo
                    </button>
                  </li>
                  <li className="nav-item">
                    <button className={`nav-link ${tab === 'signature' ? 'active' : ''}`} onClick={() => setTab('signature')}>
                      ✍️ Signature
                    </button>
                  </li>
                </ul>
                {tab === 'photo'     && <PhotoTab     orderId={order.id} onDone={handleDone} />}
                {tab === 'signature' && <SignatureTab  orderId={order.id} onDone={handleDone} />}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
